import React from "react";
import { useNavigate } from "react-router-dom";
import { useExam } from "../context/ExamContext";
import AppSidebar from "../pages/AppSidebar";

export default function Submitted() {
  const navigate = useNavigate();

  const {
    currentUser,
    lastResult,
    startNewExamFlow,
    logout,
  } = useExam();

  if (!lastResult) {
    navigate("/home", { replace: true });
    return null;
  }

  const returnHome = () => {
    startNewExamFlow();
    navigate("/home");
  };

  /*
   * -------------------------------------------------------------
   * BACKEND RESULT ONLY
   * -------------------------------------------------------------
   *
   * Examination.jsx stores the result returned by Django.
   *
   * This page does NOT:
   * - calculate percentage
   * - calculate PASS/FAIL
   * - apply CMA thresholds
   * - assume a 2-day release period
   *
   * Those rules belong to Django.
   */

  const examName =
    lastResult.examName ||
    lastResult.exam_name ||
    lastResult.courseName ||
    lastResult.course_name ||
    "Examination";

  const courseName =
    lastResult.courseName ||
    lastResult.course_name ||
    examName;

  const examDate =
    lastResult.examDate ||
    lastResult.exam_date ||
    "—";

  const autoSubmitted =
    Boolean(lastResult.autoSubmitted);

  /*
   * The backend may return a result status.
   *
   * We intentionally do not interpret PASS/FAIL here.
   * The official result is handled by the Score Board after
   * publication.
   */

  const tone = "success";

  const headline =
    "Successfully Submitted!";

  const statusLabel =
    "Submitted — Result Pending";

  const subMessage =
    "Your examination has been submitted successfully. Your official result will appear on your Score Board when it is published.";

  return (
    <div className="oes-page-full">
      <div className="oes-home-shell d-flex flex-row overflow-hidden">
        <AppSidebar />

        <div className="flex-grow-1 oes-page container">
          {/* TOP ACTIONS */}
          <div className="d-flex justify-content-end align-items-center gap-3 mb-3">
            <i className="bi bi-bell fs-5 text-secondary" />

            <button
              className="btn btn-outline-secondary btn-sm"
              onClick={() => {
                logout();
                navigate("/login");
              }}
            >
              <i className="bi bi-box-arrow-right me-1" />
              Logout
            </button>
          </div>

          {/* SUBMISSION CARD */}
          <div
            className="oes-card p-4 p-md-5 text-center"
            style={{
              maxWidth: 520,
              margin: "0 auto",
            }}
          >
            {/* ICON */}
            <div
              className={`rounded-circle bg-${tone} d-inline-flex align-items-center justify-content-center mb-3`}
              style={{
                width: 72,
                height: 72,
              }}
            >
              <i className="bi bi-check-lg text-white fs-2" />
            </div>

            {/* TITLE */}
            <h2
              className={`h5 fw-bold text-${tone} mb-1`}
            >
              {headline}
            </h2>

            <p className="text-muted mb-4">
              {subMessage}
            </p>

            {/* EXAM DETAILS */}
            <table className="table table-borderless text-start mb-3">
              <tbody>
                <tr>
                  <td className="text-muted small">
                    Course
                  </td>

                  <td>
                    : {courseName}
                  </td>
                </tr>

                <tr>
                  <td className="text-muted small">
                    Examination
                  </td>

                  <td>
                    : {examName}
                  </td>
                </tr>

                <tr>
                  <td className="text-muted small">
                    Exam Date
                  </td>

                  <td>
                    : {examDate}
                  </td>
                </tr>

                <tr>
                  <td className="text-muted small">
                    Status
                  </td>

                  <td>
                    :
                    <span
                      className={`fw-semibold text-${tone} ms-1`}
                    >
                      {statusLabel}
                    </span>
                  </td>
                </tr>

                {lastResult.attemptId && (
                  <tr>
                    <td className="text-muted small">
                      Attempt
                    </td>

                    <td>
                      : #{lastResult.attemptId}
                    </td>
                  </tr>
                )}

                {autoSubmitted && (
                  <tr>
                    <td className="text-muted small">
                      Note
                    </td>

                    <td>
                      : Auto-submitted when time
                      expired
                    </td>
                  </tr>
                )}
              </tbody>
            </table>

            {/* RESULT INFORMATION */}
            <div className="alert alert-success small d-flex align-items-start gap-2 text-start">
              <i className="bi bi-envelope-check mt-1" />

              <span>
                Your examination has been submitted
                successfully. Your official result
                will be shown on the Score Board
                according to the examination's
                configured result-release schedule.
                Your score is not displayed on this
                page.
              </span>
            </div>

            {/* EMAIL */}
            {currentUser?.email && (
              <div className="small text-muted mb-3">
                Registered email:{" "}
                <strong>
                  {currentUser.email}
                </strong>
              </div>
            )}

            {/* HOME */}
            <button
              className="btn btn-primary w-100 mt-2"
              onClick={returnHome}
            >
              RETURN TO HOME
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}